import { createClient, SupabaseClient } from '@supabase/supabase-js';
import {
  createCookieStorage,
  resolveGolfAuthAuthStorageKey,
} from './cookie-storage';
import { createClockSkewTolerantFetch } from './clock-skew-fetch';

export { createClockSkewTolerantFetch, isJwtIssuedAtFutureError } from './clock-skew-fetch';
export type { ClockSkewFetchOptions } from './clock-skew-fetch';
export {
  createCookieStorage,
  isGolfAuthStagingHost,
  resolveGolfAuthAuthStorageKey,
  resolveGolfAuthCookieDomain,
  shouldStoreSessionInCookie,
  MAX_AUTH_COOKIE_ENCODED_CHARS,
  PROD_AUTH_STORAGE_KEY,
  STAGING_AUTH_STORAGE_KEY,
} from './cookie-storage';


export interface CreateSupabaseClientOptions {
  url: string;
  anonKey: string;
  /** Use shared cookie storage for cross-subdomain auth (app.golfauth.com + fitting.golfauth.com) */
  useSharedCookies?: boolean;
  /**
   * Override storage key. Default is hostname-aware:
   * production `sb-auth-token`, staging `sb-auth-token-staging`.
   */
  storageKey?: string;
}

/**
 * Creates a Supabase client configured for GolfAuth.
 * When useSharedCookies is true and host is *.golfauth.com, uses cookie storage
 * so the session is shared across app.golfauth.com and fitting.golfauth.com
 * in production. Staging uses a separate storage key + host-only cookies so
 * it cannot share JWTs with production via Domain=.golfauth.com.
 *
 * Pass your generated `Database` (or merged app schema) as the type argument so
 * `.from()` / `.rpc()` are typed; omit it for an untyped client (e.g. Fitting).
 */
export function createSupabaseClient<Schema = any>(
  options: CreateSupabaseClientOptions,
): SupabaseClient<Schema> {
  const { url: rawUrl, anonKey: rawAnon, useSharedCookies = true } = options;
  const url = String(rawUrl ?? '').trim();
  const anonKey = String(rawAnon ?? '').trim();

  if (!url || !anonKey) {
    throw new Error('Missing Supabase URL or anon key. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
  }

  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const isGolfAuthHost =
    Boolean(hostname) &&
    (hostname.endsWith('.golfauth.com') || hostname === 'golfauth.com');

  const storageKey =
    options.storageKey ??
    (hostname ? resolveGolfAuthAuthStorageKey(hostname) : 'sb-auth-token');

  const authOptions: Record<string, unknown> = {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    flowType: 'pkce' as const,
    storageKey,
  };

  if (useSharedCookies && isGolfAuthHost) {
    const cookieStorage = createCookieStorage();
    authOptions.storage = cookieStorage;
  }

  // Realtime WSS endpoint:
  // Supabase custom domains (auth.golfauth.com) proxy REST/Auth/Storage
  // /Functions cleanly, but the WebSocket upgrade for /realtime/v1 isn't
  // forwarded by Cloudflare-style edges by default — every channel
  // subscribe loops forever in `WebSocket connection ... failed`. Detect
  // a non-native URL and steer realtime back at the project's native
  // *.supabase.co host. Project ref comes from the anon JWT's `ref`
  // claim so this self-tracks if the Supabase project ever changes.
  const realtimeConfig: Record<string, unknown> = {
    params: { eventsPerSecond: 10 },
  };
  if (shouldOverrideRealtimeUrl(url)) {
    const projectRef = deriveProjectRefFromAnonKey(anonKey);
    if (projectRef) {
      realtimeConfig.url = `wss://${projectRef}.supabase.co/realtime/v1`;
    }
  }

  return createClient<Schema>(url, anonKey, {
    auth: authOptions,
    global: {
      headers: { 'X-Client-Info': 'supabase-js-v2' },
      // Absorb mild "JWT issued at future" (PGRST303) clock skew with one
      // delayed retry — see clock-skew-fetch.ts. Applies to Core + Fitting.
      fetch: createClockSkewTolerantFetch(),
    },
    realtime: realtimeConfig,
  });
}

function shouldOverrideRealtimeUrl(supabaseUrl: string): boolean {
  try {
    const u = new URL(supabaseUrl);
    if (/\.supabase\.(co|in)$/i.test(u.hostname)) return false;
    if (['localhost', '127.0.0.1', '0.0.0.0'].includes(u.hostname)) return false;
    return true;
  } catch {
    return false;
  }
}

function deriveProjectRefFromAnonKey(anonKey: string): string | null {
  try {
    const parts = anonKey.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded =
      typeof atob === 'function'
        ? atob(padded)
        : Buffer.from(padded, 'base64').toString('utf8');
    const payload = JSON.parse(decoded) as { ref?: unknown };
    return typeof payload.ref === 'string' && payload.ref.length > 0
      ? payload.ref
      : null;
  } catch {
    return null;
  }
}

/**
 * Restore session from a token passed in the URL (e.g. from Core → Fitting navigation).
 * Call this on app load when token is present in query params.
 */
export async function setSessionFromToken(
  client: SupabaseClient,
  accessToken: string,
  refreshToken?: string
): Promise<{ error?: Error }> {
  if (!refreshToken?.trim()) {
    return {
      error: new Error(
        'Missing refresh_token: cross-app sign-in requires both token and refresh_token in the URL.'
      ),
    };
  }
  try {
    const { error } = await client.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) return { error };
    return {};
  } catch (err) {
    return { error: err instanceof Error ? err : new Error(String(err)) };
  }
}

/**
 * Build the Fitting app URL with session tokens for cross-app navigation (Core → Fitting).
 * Use when a golfer clicks "Get Fitted" in Core - they land on Fitting already logged in.
 */
export function getFittingUrl(options: {
  accessToken: string;
  refreshToken: string;
  golferId?: string;
  path?: string;
  /** Fitting app base URL (e.g. https://golfauth.com). Default from env or production URL. */
  baseUrl?: string;
}): string {
  const base = options.baseUrl ?? 'https://golfauth.com';
  const path = options.path ?? '/pro/fitting';
  const url = new URL(path, base);
  // Tokens go in the URL FRAGMENT rather than the query string.
  // Cloudflare's WAF was returning "Sorry, you have been blocked"
  // when it saw long JWTs in ?token=... (matches credential-abuse /
  // SQL-injection heuristics). Fragments never reach the server so
  // the WAF has nothing to inspect.
  const hashParams = new URLSearchParams();
  hashParams.set('token', options.accessToken);
  hashParams.set('refresh_token', options.refreshToken);
  if (options.golferId) hashParams.set('golfer_id', options.golferId);
  url.hash = hashParams.toString();
  return url.toString();
}
